import { useState, useEffect } from "react";
import { List, X, Sun, Moon } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { NAV } from "@/constants/testIds";

const consultingLinks = [
  { label: "Services", href: "#services" },
  { label: "Results", href: "#metrics" },
  { label: "Resources", href: "#resources" },
  { label: "Contact", href: "#contact" },
];

const appsLinks = [
  { label: "Mitti", href: "#mitti" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isConsulting = location.pathname === "/consulting";
  const isApps = location.pathname === "/apps";
  const navLinks = isApps ? appsLinks : consultingLinks;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid={NAV.navbar}
      className={`fixed top-0 left-0 right-0 z-50 glass-nav transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 flex items-center justify-between">
        <a
          data-testid={NAV.logo}
          href="#"
          className="flex items-center gap-3 group"
          onClick={(e) => { e.preventDefault(); navigate("/"); }}
        >
          <img
            src={theme === "light" ? "/siveli-mark-transparent.png" : "/siveli-logo-dark.png"}
            alt="Siveli Consulting"
            className="h-12 w-auto object-contain"
          />
        </a>

        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              data-testid={NAV[`${link.label.toLowerCase()}Link`]}
              href={link.href}
              className="gold-underline text-sm text-themed-muted hover:text-themed-gold transition-colors duration-200 tracking-wide"
            >
              {link.label}
            </a>
          ))}

          {/* Cross-navigation */}
          {isConsulting && (
            <button
              data-testid="nav-switch-apps"
              onClick={() => navigate("/apps")}
              className="text-sm tracking-wide transition-colors duration-200"
              style={{ color: "var(--sv-copper)" }}
            >
              Apps
            </button>
          )}
          {isApps && (
            <button
              data-testid="nav-switch-consulting"
              onClick={() => navigate("/consulting")}
              className="text-sm tracking-wide transition-colors duration-200"
              style={{ color: "var(--sv-gold)" }}
            >
              Consulting
            </button>
          )}

          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
          </button>

          <a
            data-testid={NAV.bookConsultationBtn}
            href="#contact"
            className="btn-shine text-sm font-bold tracking-wide px-5 py-2.5 transition-colors"
            style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
          >
            Book a Consultation
          </a>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
          </button>
          <button
            data-testid={NAV.mobileMenuBtn}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-themed-primary p-2"
          >
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid={NAV.mobileMenu}
          className="md:hidden absolute top-full left-0 right-0 glass-nav border-t py-6 px-6"
          style={{ borderColor: "var(--sv-border)" }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="block py-3 text-themed-secondary hover:text-themed-gold transition-colors text-base"
            >
              {link.label}
            </a>
          ))}
          {isConsulting && (
            <button onClick={() => { setMobileOpen(false); navigate("/apps"); }} className="block py-3 text-base" style={{ color: "var(--sv-copper)" }}>
              Apps
            </button>
          )}
          {isApps && (
            <button onClick={() => { setMobileOpen(false); navigate("/consulting"); }} className="block py-3 text-base" style={{ color: "var(--sv-gold)" }}>
              Consulting
            </button>
          )}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="block mt-4 text-center font-bold py-3 px-5 transition-colors"
            style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
          >
            Book a Consultation
          </a>
        </div>
      )}
    </nav>
  );
}
